import { useMemo, useState, type ReactNode } from 'react';
import {
  Alert,
  Badge,
  Button,
  Card,
  Chip,
  DataTable,
  DrawerPresence,
  ErrorBanner,
  IconButton,
  PageHeader,
  type DataTableColumn,
  type SortState,
} from '@zarya/ui';
import type { ApiError } from '@app/api/ApiError';
import { dataHour } from '@app/format';
import { t, tn } from '@app/i18n';
import { useDrawerParam } from '@app/navigation';
import { ReadingNote, readingView, useClusterReading, type ReadingState } from '@app/reading';
import { ownerName } from '@app/shared/owner';
import { DiscoveryNowButton } from './DiscoveryNowButton';
import { useEnvironmentsSelectable } from '../TenantSettings/TenantSettings.api';
import { IdentitiesFilters } from './IdentitiesFilters';
import { IdentityDrawer } from './IdentityDrawer';
import { IdentityStateBadge } from './IdentityStateBadge';
import { roleCell } from './identityVocab';
import {
  FILTERS_INICIAIS,
  hasFilter,
  identitiesKeys,
  useIdentities,
  useIdentityRoles,
  type Identity,
  type IdentitiesFilterValues,
} from './Identities.api';
import s from './Identities.module.css';
import { useMultiSite, useSiteSlugs } from '../Sites/Sites.api';

/**
 * T4 — Identidades (FR-051, FR-053).
 *
 * "Agentes descobertos no registry pelo Discovery, cada um com identidade SPIFFE
 * própria" — a lista central do produto. As colunas, os rótulos e o vazio vêm do
 * inventário do protótipo §2.6; o state exibido, da tabela do FR-051.
 *
 * O texto sai todo do catálogo (`identities.*`) desde a Onda C: nada de literal
 * aqui, nem em constante de módulo — uma constante seria resolvida na carga e não
 * repintaria na troca de idioma.
 */

/**
 * Linha de metadados do cabeçalho (FR-053).
 *
 * `total` é o do **tenant**, não o da página nem o do recorte de `escopo`:
 * "imagens acompanhadas" é quantas o Discovery acompanha, e esse número não muda
 * porque o operador olhou um lado da lista. Com o total recortado, a cena 3 — que
 * fala em "21 imagens varridas" — leria 5.
 */
function metadata(total: number, fonte?: string, varredura?: string | null): string {
  const parts = [
    fonte ? t('identities.list.meta.source', { source: fonte }) : null,
    tn('identities.list.meta.images', total),
    t('identities.list.meta.lastScan', {
      when: dataHour(varredura) ?? t('identities.list.meta.never'),
    }),
  ];
  return parts.filter(Boolean).join(' · ');
}

/**
 * A linha de metadados com a hora da foto do cluster no fim (aim-6ph.13, §2.1): «… · cluster lido às 14:05». É a
 * foto por trás da coluna «Estado». A parte da foto é um elemento próprio, com o `data-state` do protótipo; sem
 * nada a dizer, a linha fica como era.
 */
function MetadataLine({
  text,
  reading,
  pending,
}: {
  text: string;
  reading: ReadingState | undefined;
  pending: boolean;
}) {
  const note = (
    <ReadingNote
      reading={reading}
      pending={pending}
      compose={(value) => t('identities.list.meta.cluster', { reading: value })}
      testId="cluster-reading"
    />
  );
  return (
    <>
      {text}
      {/* O « · » é pontuação e fica no código, como no resto da linha (`metadata`). Só entra com a nota. */}
      {readingView(reading, { pending })?.text ? ' · ' : null}
      {note}
    </>
  );
}

function columns(open: (id: string) => void, multiSite: boolean): DataTableColumn<Identity>[] {
  return [
    // aim-pjv (decisão do mrolim, 10/10/2026): a célula mostra SÓ o slug. O SPIFFE ID, a Service Account e o
    // «atribuída pela plataforma» estão na gaveta da identidade (aba SPIFFE e a atribuição), e a coluna Tipo saiu:
    // só há agentes aqui (D6: MCP servers e APIs são Recursos).
    {
      key: 'identidade',
      header: t('identities.list.column.identity'),
      sortable: true,
      cell: (identidade) => (
        <span className={s.nome} data-testid="identity-slug">
          {identidade.slug ?? identidade.image_basename ?? '—'}
        </span>
      ),
    },
    // REVELAÇÃO PROGRESSIVA (P1): a coluna «Site» só existe quando o tenant tem
    // mais de um. Com um site só ela repetiria o mesmo valor em toda linha — ruído
    // que empurra as colunas úteis para fora da tela.
    //
    // Fica logo depois de Identidade: o site é ONDE o agente roda.
    ...(multiSite
      ? [
          {
            key: 'site',
            header: t('identities.list.column.site'),
            tip: t('identities.list.column.site.tip'),
            // O SLUG, e não o nome: é ele que entra no SPIFFE ID e o que o operador
            // vê no `kubectl`. O nome é livre e pode repetir.
            cell: (identidade: Identity) => <Chip>{identidade.site_slug}</Chip>,
          } satisfies DataTableColumn<Identity>,
        ]
      : []),
    {
      key: 'papel',
      header: t('identities.list.column.role'),
      tip: t('identities.list.column.role.tip'),
      // aim-pjv: o papel (com a especialidade, quando há) em até 30 caracteres, e o valor inteiro na dica. O
      // badge «papel novo» saiu (decisão do mrolim, 10/10/2026).
      cell: (identidade) => {
        if (!identidade.role) return <span className={s.vazio}>—</span>;
        const role = roleCell(
          identidade.specialty ? `${identidade.role} · ${identidade.specialty}` : identidade.role,
        );
        return (
          <span data-testid="identity-role" title={role.full ?? undefined}>
            <Chip>{role.text}</Chip>
          </span>
        );
      },
    },
    {
      key: 'dono',
      header: t('identities.list.column.owner'),
      tip: t('identities.list.column.owner.tip'),
      // Só o nome na célula: o `Nome <e-mail>` inteiro dobraria a largura da
      // coluna, e o e-mail é o que menos se lê aqui. Ele fica no `title`, que é
      // onde se confere qual das duas pessoas de mesmo nome é.
      cell: (identidade) =>
        identidade.owner ? (
          <span title={identidade.owner}>{ownerName(identidade.owner)}</span>
        ) : (
          <span className={s.vazio}>—</span>
        ),
    },
    {
      key: 'imagem',
      header: t('identities.list.column.image'),
      // Duas linhas: o basename distingue a linha, o host/owner é igual em todas
      // (e ocupava metade da largura da grid quando repetido inteiro).
      cell: (identidade) => {
        if (!identidade.image) return <span className={s.vazio}>—</span>;
        const cut = identidade.image.lastIndexOf('/');
        const prefix = cut > 0 ? identidade.image.slice(0, cut) : null;
        const basename = cut > 0 ? identidade.image.slice(cut + 1) : identidade.image;
        return (
          <span className={s.duasLinhas} title={identidade.image}>
            <span className={s.monoForte}>{basename}</span>
            {prefix ? <span className={s.sub}>{prefix}</span> : null}
          </span>
        );
      },
    },
    {
      key: 'ambiente',
      header: t('identities.list.column.environment'),
      // A fala da cena 3 cita o ambiente e a cena 5 depende dele — e ele não
      // estava na lista (ensaio de 30/08, duas vezes). Fica ao lado de Estado
      // porque é a mesma família: os dois decidem o regime de aprovação da
      // próxima release (FR-066).
      //
      // Só o valor, sem a origem: `ambiente_origem` vem `null` em
      // `listIdentities` de propósito — derivá-lo por linha seria uma leitura de
      // releases por linha. Quem quer saber de onde veio abre o drawer, onde T5
      // o diz ao lado do campo.
      tip: t('identities.list.column.environment.tip'),
      cell: (identidade) =>
        identidade.environment ? (
          <Chip>{identidade.environment}</Chip>
        ) : (
          <span
            className={s.subTexto}
            title={t('identities.list.environmentUndetermined.title')}
          >
            {t('identities.list.environmentUndetermined')}
          </span>
        ),
    },
    {
      key: 'state',
      header: t('identities.list.column.state'),
      sortable: true,
      // A explicação de cada state fica no cabeçalho: repetir o ⓘ em toda linha
      // roubava a largura das colunas seguintes (FR-132 pede a explicação, não
      // uma por linha).
      tip: t('identities.list.column.state.tip'),
      // Com ⓘ (ajustes-front #7): «Descoberta · sem contrato» em T4 não se explica
      // sozinho — a pessoa precisa saber que a release não gerou pendência e por quê.
      cell: (identidade) => <IdentityStateBadge state={identidade.state} />,
    },
    {
      key: 'politicas',
      header: t('identities.list.column.policies'),
      tip: t('identities.list.column.policies.tip'),
      cell: (identidade) => {
        const politicas = identidade.policies ?? 0;
        return politicas > 0 ? (
          <Chip icon="shield">{politicas}</Chip>
        ) : (
          <span className={s.vazio}>—</span>
        );
      },
    },
    {
      key: 'security_ready',
      header: t('identities.list.column.securityReady'),
      tip: t('identities.list.column.securityReady.tip'),
      cell: (identidade) =>
        identidade.security_ready === true ? (
          <Badge tone="success">{t('identities.list.securityReady.ready')}</Badge>
        ) : identidade.security_ready === false ? (
          <Badge tone="danger">{t('identities.list.securityReady.notReady')}</Badge>
        ) : (
          <Badge tone="neutral">{t('identities.list.securityReady.unknown')}</Badge>
        ),
    },
    {
      key: 'acoes',
      header: '',
      width: '48px',
      cell: (identidade) => (
        <span className={s.acoes}>
          <IconButton
            icon="arrow-right"
            label={t('identities.list.openRow', {
              name:
                identidade.slug ??
                identidade.image_basename ??
                t('identities.list.openRow.fallback'),
            })}
            size="sm"
            onClick={(event) => {
              event.stopPropagation();
              open(identidade.id);
            }}
          />
        </span>
      ),
    },
  ];
}

export function IdentitiesPage() {
  const [filters, setFiltros] = useState<IdentitiesFilterValues>(FILTERS_INICIAIS);
  const [erroDiscovery, setErroDiscovery] = useState<ApiError | null>(null);
  const [avisoDiscovery, setAvisoDiscovery] = useState<string | null>(null);
  const drawer = useDrawerParam();

  const identidades = useIdentities(filters);
  // Abrir T4 pede a leitura nova do cluster (decisão 2): a lista mostra a foto na hora, e a coluna «Estado» se
  // atualiza quando ela termina (o `refetchInterval` de `useIdentities`).
  const cluster = useClusterReading({ scope: 'cluster', refresh: [identitiesKeys.todas] });
  const roleList = useIdentityRoles();
  // Revelação progressiva (P1): só com mais de um site a coluna e o filtro «Site»
  // aparecem.
  const multiSite = useMultiSite();
  const siteSlugs = useSiteSlugs();
  // `Selectable` e não a lista inteira: o eixo C nunca é ambiente de identidade, e
  // uma opção que devolve sempre zero linhas ensina o contrário de como o produto
  // funciona.
  const environments = useEnvironmentsSelectable();
  const environmentOptions = useMemo(
    () => (environments.data ?? []).map((e) => ({ slug: e.slug, name: e.name })),
    [environments.data],
  );

  const itens = identidades.data?.items ?? [];
  const total = identidades.data?.total ?? 0;
  const meta = identidades.data?.meta;
  // O total do TENANT: as duas metades somadas, e não o `total` da resposta, que
  // é o do recorte corrente.
  const totalDoTenant = (meta?.governed ?? 0) + (meta?.discovered ?? 0);
  const filtrando = hasFilter(filters);

  /** Toda mudança de filtro volta para a primeira página. */
  const changeFilter = (parcial: Partial<IdentitiesFilterValues>) =>
    setFiltros((current) => ({ ...current, ...parcial, page: parcial.page ?? 1 }));

  /**
   * Estado de ordenação da tabela, derivado do valor do contrato.
   *
   * Só duas colunas ordenam, porque só duas têm valor no enum `sort` de
   * `listIdentities`: Identidade (`slug`/`-slug`) e Estado (`estado`). **Não
   * existe `-estado`** — a ordem dos estados é a tabela do FR-051, que tem uma
   * direção canônica; então a coluna Estado alterna entre ordenada e a ordem
   * padrão do servidor, e o `aria-sort` volta a `none` junto. Oferecer uma
   * descendente que o servidor recusa seria a mesma mentira que o `sortable:
   * true` sem `onSortChange` era.
   */
  const tableOrder: SortState | null =
    filters.sort === 'slug'
      ? { key: 'identidade', direction: 'asc' }
      : filters.sort === '-slug'
        ? { key: 'identidade', direction: 'desc' }
        : filters.sort === 'state'
          ? { key: 'state', direction: 'asc' }
          : null;

  /**
   * O vazio da tabela, e o caso que o recorte de `escopo` criou (30/08).
   *
   * **«Nenhuma identidade descoberta ainda» seria mentira** quando a lista está
   * vazia porque o operador está em «Governadas» e as identidades que existem
   * estão todas do outro lado — que é exatamente o state do laboratório ao abrir
   * a apresentação: zero governadas, dezesseis descobertas. Dizer "rode o
   * Discovery" ali manda repetir uma varredura que já achou tudo.
   *
   * Por isso o vazio nomeia o outro lado e leva até ele. É o que impede o recorte
   * de virar sumiço no único lugar onde ele viraria.
   */
  const empty = ((): { titulo: string; mensagem: string; acao: ReactNode } => {
    const otherSide =
      filters.scope === 'governadas'
        ? {
            rotulo: t('identities.filters.scope.discovered'),
            quantas: meta?.discovered ?? 0,
            ir: 'descobertas' as const,
          }
        : {
            rotulo: t('identities.filters.scope.governed'),
            quantas: meta?.governed ?? 0,
            ir: 'governadas' as const,
          };

    if (!filtrando && filters.scope !== null && otherSide.quantas > 0) {
      return {
        titulo: t('identities.list.empty.scope.title', {
          scope:
            filters.scope === 'governadas'
              ? t('identities.filters.scope.governed')
              : t('identities.filters.scope.discovered'),
        }),
        mensagem: tn('identities.list.empty.scope.message', otherSide.quantas, {
          other: otherSide.rotulo,
        }),
        acao: (
          <Button variant="out" onClick={() => changeFilter({ scope: otherSide.ir })}>
            {t('identities.list.empty.scope.action', {
              other: otherSide.rotulo.toLowerCase(),
              n: otherSide.quantas,
            })}
          </Button>
        ),
      };
    }
    if (filtrando) {
      return {
        titulo: t('identities.list.empty.filtered.title'),
        mensagem: t('identities.list.empty.filtered.message'),
        acao: null,
      };
    }
    return {
      titulo: t('identities.list.empty.none.title'),
      mensagem: t('identities.list.empty.none.message'),
      acao: <DiscoveryNowButton onErro={setErroDiscovery} onAviso={setAvisoDiscovery} />,
    };
  })();

  const changeOrder = ({ key, direction }: SortState) => {
    if (key === 'identidade') {
      changeFilter({ sort: direction === 'asc' ? 'slug' : '-slug' });
      return;
    }
    if (key === 'state') {
      changeFilter({ sort: filters.sort === 'state' ? null : 'state' });
    }
  };

  return (
    <>
      <PageHeader
        title={t('identities.list.title')}
        tip={t('identities.list.purpose')}
        icon="robot"
        updatedLabel={
          <MetadataLine
            text={metadata(totalDoTenant, meta?.source, meta?.last_scan_at)}
            reading={meta?.reading}
            pending={cluster.pending}
          />
        }
        actions={<DiscoveryNowButton onErro={setErroDiscovery} onAviso={setAvisoDiscovery} />}
      />

      {erroDiscovery ? (
        <ErrorBanner
          error={erroDiscovery}
          title={t('identities.list.discoveryError.title')}
          escrita
          onRetry={() => setErroDiscovery(null)}
          retryLabel={t('identities.list.discoveryError.close')}
        />
      ) : null}

      {avisoDiscovery ? (
        <Alert tone="warning" title={t('identities.list.discoveryWarning.title')}>
          {avisoDiscovery}
        </Alert>
      ) : null}

      <Card
        title={t('identities.list.card.title')}
        subtitle={tn('identities.list.card.subtitle', total, { shown: itens.length })}
        bare
      >
        <DataTable<Identity>
          label={t('identities.list.table.label')}
          columns={columns(drawer.abrir, multiSite)}
          rows={itens}
          rowKey={(identidade) => identidade.id}
          loading={identidades.isLoading}
          error={identidades.isError ? identidades.error : null}
          onRetry={() => void identidades.refetch()}
          sort={tableOrder}
          onSortChange={changeOrder}
          search={filters.q}
          onSearchChange={(q) => changeFilter({ q })}
          searchPlaceholder={t('identities.list.searchPlaceholder')}
          filters={
            <IdentitiesFilters
              roles={roleList.data ?? []}
              papelSelecionado={filters.papeis[0] ?? null}
              onPapelChange={(papel) => changeFilter({ papeis: papel ? [papel] : [] })}
              state={filters.state}
              onEstadoChange={(state) => changeFilter({ state })}
              scope={filters.scope}
              onEscopoChange={(scope) => changeFilter({ scope })}
              governadas={meta?.governed ?? 0}
              descobertas={meta?.discovered ?? 0}
              // Lista vazia sem `multi`: a revelação progressiva vale para o filtro
              // como vale para a coluna.
              sites={siteSlugs}
              site={filters.site}
              onSiteChange={(site) => changeFilter({ site })}
              environments={environmentOptions}
              environment={filters.environment}
              onEnvironmentChange={(environment) => changeFilter({ environment })}
            />
          }
          filtersApplied={filtrando}
          // Limpar **filtros** não desfaz a ordenação nem o recorte: nenhum dos
          // dois é um dos filtros que o botão anuncia, e perder a ordem — ou ser
          // jogado de volta para «Governadas» estando em «Descobertas» — ao limpar
          // uma busca surpreende. O caminho de volta do recorte é a própria pílula.
          onClearFilters={() =>
            setFiltros({ ...FILTERS_INICIAIS, sort: filters.sort, scope: filters.scope })
          }
          page={filters.page}
          total={total}
          onPageChange={(page) => setFiltros((current) => ({ ...current, page }))}
          onRowClick={(identidade) => drawer.abrir(identidade.id)}
          emptyIcon="robot"
          emptyTitle={empty.titulo}
          emptyMessage={empty.mensagem}
          emptyAction={empty.acao}
        />
      </Card>

      <DrawerPresence>
        {drawer.id ? <IdentityDrawer identityId={drawer.id} onClose={drawer.fechar} /> : null}
      </DrawerPresence>
    </>
  );
}
